import {
  Component,
  computed,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  HsFormFieldModule,
  HsInputModule,
  HsSelectModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HsLayerModule } from '@hsetu/ngui-resources';
import { ICON_PROCESS } from '@shared/ui/icons/icon-constants';
import { StatusHistory } from '@modules/status-history/models/status-history.model';
import { HistoryStatus } from '@modules/sales/enums/history-status.enum';
import {
  STATUS_CUSTOM,
  STATUS_NONE,
  STATUS_IM_AUFBAU,
  STATUS_STORNIERT,
  STATUS_GEBUCHT,
  STATUS_DURCHGEFUEHRT,
  STATUS_NEUER_TERMIN,
  STATUS_AKZEPTIERT,
  STATUS_UEBERFAELLIG,
  STATUS_NICHT_ANGETROFFEN,
  STATUS_ABGESCHLOSSEN,
} from '@modules/status-history/constants/status-constants';
import { StatusHistoryService } from '@modules/status-history/services/status-hsitory.service';
import { StatusHistoryMapper } from '@modules/status-history/mappers/status-history.mapper';
import { History } from '@modules/status-history/models/history.model';
import { KfmCardComponent } from '@shared/ui/components/features/card/common/kfm-card.component';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import {
  CHOOSE_STATUS_LABEL,
  STATUS_HISTORY_LABEL,
} from '@modules/status-history/i18n/label-text-constants';

@Component({
  selector: 'kfm-status-history-card',
  imports: [
    CommonModule,
    HsFormFieldModule,
    HsLayerModule,
    HsInputModule,
    HsSelectModule,
    KfmCardComponent,
  ],
  templateUrl: './kfm-status-history-card.component.html',
})
export class KfmStatusHistoryCardComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _statusHistoryService = inject(StatusHistoryService);
  private readonly _mapper = new StatusHistoryMapper();
  private readonly _datePipe = new DatePipe('de-DE');
  private readonly _guidGenerator = inject(GuidGeneratorService);

  private readonly _history = signal<History>(undefined);
  private readonly _statusOptions = signal<StatusHistory[]>([]);

  protected readonly cardTitle = STATUS_HISTORY_LABEL;
  protected readonly chooseStatusLabel = CHOOSE_STATUS_LABEL;

  protected readonly selectedOption = signal<string>(undefined);

  protected stati = computed((): HsSelectOption<string>[] => {
    const statusOptions = this._statusOptions();

    return statusOptions
      .map((s) => this.getStatusDescription(s))
      .sort((a, b) => a.localeCompare(b))
      .map((desc) => ({ value: desc, label: desc }));
  });

  protected readonly cardIcon = computed<SafeHtml>(() =>
    this._sanitizer.bypassSecurityTrustHtml(ICON_PROCESS),
  );

  protected readonly internalHistory = computed<StatusHistory[]>(() => {
    const history = this._history();
    return history && history.stati ? history.stati : [];
  });

  @Input({ required: true })
  public set history(value: History) {
    this._history.set(value);
    this.sortStatusHistoryByTaskType();
  }

  public isEditable = input<boolean>(true);

  public historyChanged = output<History>();

  constructor() {
    const enumNums: number[] = Object.keys(HistoryStatus)
      .filter((key) => !isNaN(Number(key)))
      .map(Number);
    const options = [];

    enumNums.forEach((status: number) => {
      options.push({
        employeeDesc: '',
        uId: '',
        infoDesc: this._mapper.getLongDesc(status),
        shortDesc: this._mapper.getShortDesc(status),
        statusId: this._mapper.getGuid(status),
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        concurrencyStamp: new Date().toISOString(),
        stampCreate: new Date().toISOString(),
        syncType: 1,
        tenantUId: '',
        userUId: '',
      });
    });

    this._statusOptions.set(options);
  }

  protected getStatusDescription(status: StatusHistory): string {
    const historyStatus: HistoryStatus = this._mapper.getByGuid(
      status.statusId,
    );

    return this._mapper.getLongDesc(historyStatus);
  }

  protected getddMMyyyyFormat(date: Date | string) {
    const dt = new Date(date);
    const result: string | null = this._datePipe.transform(dt, 'dd.MM.yyyy');

    return result ? result : dt.toISOString();
  }

  protected async onStatusChanged(selected: HsSelectOption<string>) {
    const selectedStatus = selected.value;
    const history = this._history();

    const statusOptions = this._statusOptions();
    const status = statusOptions.find(
      (s) => this.getStatusDescription(s) === selectedStatus,
    );

    const historyStatus: HistoryStatus = this._mapper.getByGuid(
      status.statusId,
    );

    // TODO get employee name from token

    const statusHistoryData = this._statusHistoryService.getTemplate(
      '',
      historyStatus,
      history.ownerId,
    );

    const result: StatusHistory | undefined =
      await this._statusHistoryService.create(statusHistoryData);

    if (history.stati) {
      // insert the item
      this._history.update((history) => ({
        ...history,
        stati: [result, ...history.stati],
      }));
    } else {
      this._history.update((history) => ({
        ...history,
        stati: [result],
      }));
    }

    this.historyChanged.emit(this._history());

    this.selectedOption.set(undefined);
  }

  private sortStatusHistoryByTaskType(): void {
    const options = this._statusOptions();
    const history = this._history();

    if (!history || !history.stati || history.stati.length === 0) return;

    // Extend filtering logic based on task.type
    if (history.ownerType === 3) {
      this._statusOptions.set(
        options.filter(
          (s) =>
            s.statusId !== STATUS_CUSTOM &&
            s.statusId !== STATUS_NONE &&
            s.statusId !== STATUS_IM_AUFBAU &&
            s.statusId !== STATUS_STORNIERT &&
            s.statusId !== STATUS_GEBUCHT &&
            s.statusId !== STATUS_DURCHGEFUEHRT &&
            s.statusId !== STATUS_NEUER_TERMIN &&
            s.statusId !== STATUS_AKZEPTIERT &&
            s.statusId !== STATUS_UEBERFAELLIG &&
            s.statusId !== STATUS_NICHT_ANGETROFFEN,
        ),
      );
    } else {
      this._statusOptions.set(
        options.filter(
          (s) =>
            s.statusId !== STATUS_CUSTOM &&
            s.statusId !== STATUS_NONE &&
            s.statusId !== STATUS_IM_AUFBAU &&
            s.statusId !== STATUS_ABGESCHLOSSEN &&
            s.statusId !== STATUS_GEBUCHT,
        ),
      );
    }
  }
}
